import { onMounted, onUnmounted, ref, Ref } from 'vue';
import throttle from 'lodash/throttle';

import { useAuthStore } from '@/stores/auth.store';
import { lastActivityStorage } from '@/storages/browser.storage';
import { onSession, postSession } from '@/utils/session-channel';
import {
  ACTIVITY_EVENTS,
  ACTIVITY_THROTTLE_MS,
  INACTIVITY_LOGOUT_MS,
  INACTIVITY_POLL_MS,
  INACTIVITY_WARNING_MS,
} from '@/app-constants/session.constant';

type UseInactivityMonitor = {
  showWarning: Ref<boolean>;
  secondsLeft: Ref<number>;
  stayLoggedIn: () => Promise<void>;
};

/**
 * Polls the last-activity timestamp every 30s and drives a 3-band state machine:
 *   idle >= 29m  → signout (enforced even if the modal is never seen)
 *   idle >= 25m  → show warning + countdown
 *   otherwise    → dismiss
 * Polling a wall-clock timestamp survives background-tab throttling and sleep,
 * and reads cross-tab activity automatically.
 */
export const useInactivityMonitor = (): UseInactivityMonitor => {
  const { refreshAccessToken, signout } = useAuthStore();

  const showWarning = ref<boolean>(false);
  const secondsLeft = ref<number>(0);

  let pollId: ReturnType<typeof setInterval> | undefined;
  let removeChannelListener: (() => void) | undefined;

  const idleMs = (): number => {
    const stored = lastActivityStorage.get();
    // Stored value is parsed JSON with no runtime guarantee: a missing/non-numeric
    // timestamp is treated as "active now" so we never falsely log a user out.
    const lastActivity = typeof stored === 'number' ? stored : Date.now();
    return Date.now() - lastActivity;
  };

  const evaluate = (): void => {
    const idle = idleMs();
    if (idle >= INACTIVITY_LOGOUT_MS) {
      showWarning.value = false;
      signout();
      return;
    }
    if (idle >= INACTIVITY_WARNING_MS) {
      showWarning.value = true;
      secondsLeft.value = Math.max(0, Math.ceil((INACTIVITY_LOGOUT_MS - idle) / 1000));
      return;
    }
    showWarning.value = false; // activity resumed (this or another tab) → dismiss
  };

  // Same-tab activity must dismiss the warning immediately. Cross-tab activity
  // already re-evaluates via the channel handler in onMounted, but
  // BroadcastChannel never echoes to the sender, so this tab needs its own
  // listener. Throttled and gated on `showWarning` so it's a cheap no-op
  // outside the warning window.
  const recheckOnLocalActivity = throttle(
    (): void => {
      if (showWarning.value) {
        evaluate();
      }
    },
    ACTIVITY_THROTTLE_MS,
    { leading: true, trailing: true },
  );

  // Stable wrapper with a plain listener type so add/removeEventListener share
  // one reference and lodash's throttle typing doesn't leak to the DOM API.
  const handleLocalActivity = (): void => {
    recheckOnLocalActivity();
  };

  const stayLoggedIn = async (): Promise<void> => {
    try {
      const token = await refreshAccessToken(); // RT exchange resets the RT idle clock
      if (!token) {
        return; // definitive error → already signed out; leave the rest to the poll
      }
      const ts = Date.now();
      lastActivityStorage.set(ts);
      postSession({ type: 'activity', ts });
      showWarning.value = false;
    } catch (error) {
      // Transient failure with retries exhausted: keep the warning open so the
      // user can click again; the poll still enforces hard logout at the threshold.
      console.warn('[session] "Stay logged in" refresh failed; will retry on next click', error);
    }
  };

  onMounted(() => {
    evaluate();
    pollId = setInterval(evaluate, INACTIVITY_POLL_MS);
    ACTIVITY_EVENTS.forEach((eventName) => window.addEventListener(eventName, handleLocalActivity, { passive: true }));
    // Another tab seeing activity should dismiss our modal instantly.
    removeChannelListener = onSession((message) => {
      if (message.type === 'activity') {
        evaluate();
      }
    });
  });

  onUnmounted(() => {
    if (pollId) {
      clearInterval(pollId);
    }
    ACTIVITY_EVENTS.forEach((eventName) => window.removeEventListener(eventName, handleLocalActivity));
    recheckOnLocalActivity.cancel();
    removeChannelListener?.();
  });

  return { showWarning, secondsLeft, stayLoggedIn };
};
